import {
  Box,
  Grid,
  Typography,
  Link,
  TextField,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import CustomInfoShieldIcon from "./CustomInfoShieldIcon";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { MultiInput } from "@/Components/Multi-Input";

import "./newAvatarScreen.scss";
import { useEffect, useState, useMemo } from "react";
import { Outcomes, Phases } from "@/telemetry/events";
import {
  abandonOnUnmount,
  enterPhase,
  exitActivePhase,
  exitPhase,
  getActivePhase,
} from "@/telemetry/phases";
import GenderModal from "../../Components/Modal/GenderModal";
import AgeModal from "../../Components/Modal/AgeModal";
import HeightModal from "../../Components/Modal/HeightModal";
import WeightModal from "../../Components/Modal/WeightModal";
import { Avatar, Errors } from "../../types/interfaces";
import {
  convertCmToFeetInches,
  convertKgToLb,
  getAvatarFromLocalStorage,
  stringToBoolean,
} from "../../Utils";
import { collection, getDocs, where, query } from "firebase/firestore";
import { db } from "../../Utils/firebase";
import { pink } from "@mui/material/colors";
import DiscardModal from "@/Components/Modal/DiscardModal";
import CancelModal from "@/Components/Modal/CancelModal";
import { useSetAtom } from "jotai";
import { scanStatusAtom } from "@/atoms/scanning";
import { ScanUploader, UploadEvents } from "@/services/ScanUploader";
import { logInfo, logError } from "@/Utils/logger";
import { isProdEnv } from "@/Utils/constants";
import { watchOrphanedScanState } from "@/Utils/scanRecovery";
import {
  SCAN_SUBMITTED_ACK_TTL_MS,
  clearScanSubmittedMarker,
  readScanSubmittedMarker,
} from "@/Utils/scanSubmittedMarker";
import {
  getAvatarSharingData,
  setAvatarSharingData,
  hasActiveSharingSession,
  isDeeplinkSession,
  clearAvatarSharingData,
  isStyleMatchClientId,
  isSisleyClientId,
  getSharedScanId,
  setSharedScanId,
  setDeeplinkFlag,
  shareRecipientLabel,
} from "@/Utils/avatarSharingStorage";
import type { AvatarSharingData } from "@/Utils/avatarSharingStorage";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  resolveShareOrgName,
  resolveSharedScanShare,
} from "@/Utils/sharedScanApproval";

const isDev = process.env.NODE_ENV === "development";
const isMalchusEnv = process.env.REACT_APP_IS_MALCHUS_ENV === "true";
const isNeemsEnv = process.env.REACT_APP_IS_NEEMS_ENV === "true";
const isStyleMatchEnv = process.env.REACT_APP_IS_STYLEMATCH_ENV === "true";
const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

type ShareCardProps = {
  deepLink: boolean;
  handleClientIdChange: (value: string) => void;
  multiInputDefaultValue: string;
  error: string | null;
  /** True once the share has been binned here and Undo is on offer. */
  shareCancelled: boolean;
  onCancelShare: () => void;
  onRestoreShare: () => void;
};

const ShareCard = ({
  deepLink,
  handleClientIdChange,
  multiInputDefaultValue,
  error,
  shareCancelled,
  onCancelShare,
  onRestoreShare,
}: ShareCardProps) => {
  useEffect(() => {
    if (isMalchusEnv) {
      const sharingData = getAvatarSharingData();
      if (!sharingData.orgName || !sharingData.clientId) {
        // set them to malchus values
        setAvatarSharingData({
          orgName: "Malchus Clothiers LLC",
          clientId: "715053",
        });
      }
    }
    if (isNeemsEnv) {
      const sharingData = getAvatarSharingData();
      if (!sharingData.orgName || !sharingData.clientId) {
        // set them to neems values
        setAvatarSharingData({
          orgName: "Neems Jeans",
          clientId: "553777",
        });
      }
    }
    if (isSisleyEnv) {
      const sharingData = getAvatarSharingData();
      // Seed the walk-in default, but never clobber a valid Sisley clientId
      // (e.g. a 454934 sub-account deep-link session) back to parent 417017.
      // The old `!orgName || !clientId` test did exactly that whenever a sub's
      // deep link arrived before orgName was resolved.
      if (!isSisleyClientId(sharingData.clientId)) {
        setAvatarSharingData({
          orgName: "Sisley Clothing Australia",
          clientId: "417017",
        });
      } else if (!sharingData.orgName) {
        setAvatarSharingData({ orgName: "Sisley Clothing Australia" });
      }
    }
    if (isStyleMatchEnv) {
      const sharingData = getAvatarSharingData();
      // Seed the walk-in default, but never clobber a valid StyleMatch clientId
      // (e.g. a 542101 staging deep-link session) back to prod 809856.
      if (!isStyleMatchClientId(sharingData.clientId)) {
        setAvatarSharingData({
          orgName: "StyleMatch",
          clientId: "809856",
        });
      } else if (!sharingData.orgName) {
        setAvatarSharingData({ orgName: "StyleMatch" });
      }
    }
  }, []);

  if (isMalchusEnv) return null;
  if (isNeemsEnv) return null;
  if (isStyleMatchEnv) return null;
  if (isSisleyEnv) return null;
  return (
    <>
      {deepLink === true ? (
        <div className="step">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <span className="step-count">3</span>{" "}
              </Grid>
              <Grid item xs>
                <span className="step-name">Share</span>
              </Grid>
              <Grid item>
                <button
                  type="button"
                  className="share-cancel"
                  aria-label="Cancel sharing"
                  onClick={onCancelShare}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              padding: 2,
            }}
          >
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontWeight: "400",
                  color: "var(--sm-text-muted, rgba(138, 138, 152, 1))",
                }}
                variant="body1"
                component="div"
              >
                Once successfully processed, your avatar and its data will be
                sent to: <br />
                <strong>{shareRecipientLabel(getAvatarSharingData())}</strong>
              </Typography>
            </Grid>
          </Box>
        </div>
      ) : (
        <div className="step">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <span className="step-count">3</span>{" "}
              </Grid>
              <Grid item xs>
                <span className="step-name">Share</span>
              </Grid>
              {shareCancelled ? (
                <Grid item>
                  <button
                    type="button"
                    className="share-redo"
                    onClick={onRestoreShare}
                  >
                    Undo
                  </button>
                </Grid>
              ) : (
                <Grid item>Optional</Grid>
              )}
            </Grid>
          </Box>
          <Box
            sx={{
              padding: 2,
            }}
          >
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontWeight: "400",
                  color: "var(--sm-text-muted, rgba(138, 138, 152, 1))",
                }}
                variant="body1"
                component="div"
              >
                Send this avatar to another TrueToForm user using their 6-digit
                share code
              </Typography>
            </Grid>
            <div className="share-code">
              <MultiInput
                onChange={handleClientIdChange}
                error={error}
                inputClassName="share-code-input h1"
                containerClassName="input-container"
                defaultValue={multiInputDefaultValue}
              />
              <p className="error-message p1">{error}</p>
            </div>
          </Box>
        </div>
      )}
    </>
  );
};

const NewAvatarScreen = () => {
  // `form` phase: the user is filling in age/height/weight/gender.
  // Entering auto-exits the preceding phase with `advanced`. Submit closes it
  // as `advanced` explicitly (see `goToSubmit`); any other way off the screen
  // is an abandonment. Verified by `formPhase.test.tsx`.
  useEffect(() => {
    // Except a scan phase that is still open. The scan screen's X, or the
    // system Back, lands here, and a successful scan has already closed its
    // phase before the app navigates. So an open setup, alignment or rotation
    // means the user left the scan screen without finishing, and the auto-exit
    // would record that as `advanced`. On prod, 2026-08-31 to 09-14: 59 of 772
    // alignment exits and 703 of 1,201 setup exits (513 share-link visitors
    // closing the setup checklist with X). None was an automatic redirect.
    const open = getActivePhase();
    if (
      open === Phases.SCAN_SETUP ||
      open === Phases.ALIGNMENT ||
      open === Phases.ROTATION
    ) {
      exitActivePhase(Outcomes.ABANDONED);
    }
    enterPhase(Phases.FORM);
    // Called inside the cleanup, not returned from the effect body — see the
    // note in telemetry/useScanPhases.ts.
    return () => {
      abandonOnUnmount([Phases.FORM]);
    };
  }, []);

  const { navigateTo, navigateBackTo } = useCustomNavigation();
  const setScanStatus = useSetAtom(scanStatusAtom);
  const scanUploader = useMemo(() => ScanUploader.getInstance(), []);

  // Determine if this is a deepLink session by checking storage
  const sharingData = getAvatarSharingData();
  const isDeeplink = isDeeplinkSession();

  // deepLink is true if:
  // 1. It's a deeplink session (from /send-avatar route), OR
  // 2. There's an active sharing session with valid clientId
  const deepLink =
    isDeeplink ||
    (hasActiveSharingSession() &&
      (isMalchusEnv
        ? sharingData.clientId === "715053"
        : isNeemsEnv
          ? sharingData.clientId === "553777"
          : isStyleMatchEnv
            ? isStyleMatchClientId(sharingData.clientId)
            : true));

  const [compatible, setCompatible] = useState(true);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [showHeightModal, setShowHeightModal] = useState(false);
  const [showWeightModal, setShowWeightModal] = useState(false);
  // Sisley requested that height/weight default to metric (cm / kg). These four
  // unit states seed the metric default for the Sisley tenant when the user has
  // no saved preference yet; a stored convertHeight/convertWeight (from a prior
  // visit, loaded below) still overrides it, and the in-modal toggles keep
  // working. Every other tenant keeps the original imperial default.
  const [unit, setUnit] = useState<"cm" | "inch">(isSisleyEnv ? "cm" : "inch");
  const [convertWeight, setConvertWeight] = useState(isSisleyEnv);
  const [convertHeight, setConvertHeight] = useState(isSisleyEnv);
  const [scanError, setScanError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Photo opt-in. Default: on in dev (so the consented path is the one you get
  // by default while testing), off in prod.
  //
  // Component state only — deliberately NOT persisted anywhere. No
  // localStorage, no profile field: on prod every scan starts unchecked, so
  // consent is always given fresh and is never inherited from a previous scan.
  //
  // Recorded on submit as BOTH scans/{scanId}.storePhotosEnabled (retention +
  // troubleshooting) and .sharePhotosEnabled (the accounts this scan is shared
  // with may view and export the photos). See ScanModel.create.
  const [storePhotosEnabled, setStorePhotosEnabled] = useState<boolean>(
    !isProdEnv,
  );
  const [weightUnit, setWeightUnit] = useState<"lb" | "kg">(
    isSisleyEnv ? "kg" : "lb",
  );
  const [open, setOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // The Scan step and validate() trust localStorage scanID, but after a
  // reload/browser-close the in-memory ScanUploader is empty, so a Submit
  // with a leftover scanID navigates to an eternal "Submitting scan" screen
  // (submitScan() on an empty uploader can never finalize — pwa_logs
  // signature: handleSubmit with scanUploaderScanId: ""). Reconcile the
  // leftover state against upload_processing on mount; whether the scan is
  // dead OR already submitted, dropping the keys makes step 1 honestly show
  // "Start" again for a fresh scan. The dummy state bump forces a re-render
  // because the JSX reads localStorage directly. Guarded inside the helper:
  // never touches a scan this tab's uploader is working on.
  const [, setScanStateRecoveredTick] = useState(0);
  useEffect(
    () =>
      watchOrphanedScanState("NewAvatarScreen", () =>
        setScanStateRecoveredTick((n) => n + 1),
      ),
    [],
  );

  // The mirror image of the case above: keys that were CORRECTLY removed,
  // because the scan succeeded. `reset()` drops scanID on success, so the
  // Step-1 check below would otherwise render "never started" on every arrival
  // here after a submit — back gesture, refresh, new tab — and the user
  // concludes the scan was lost. Read during render, like the scanID check it
  // sits beside, so both refresh through the same tick.
  const justSubmitted = readScanSubmittedMarker();

  // ...but a live marker is not, by itself, a reason to SAY anything. It only
  // means a scan succeeded recently; it does not mean the user is unaware of
  // it. Someone who opened their avatars list — where that scan is visible as
  // Processing — and pressed "New Avatar" is deliberately starting another
  // scan, and telling them "Submitted" over the form they just asked for reads
  // as a glitch rather than reassurance. Those controls acknowledge the marker
  // (see `acknowledgeScanSubmitted`), so the reassurance is left for the
  // arrivals that actually need it: a stray back gesture, a reload or a new
  // tab, and the redirect/share exit, none of which pass through that button.
  //
  // The marker itself is untouched by the acknowledgement, so the mount log
  // below still counts every empty-form-after-a-success arrival at the same
  // rate as before this change.
  //
  // FROZEN AT MOUNT, unlike the marker beside it. The acknowledgement expires
  // on its own short clock (`SCAN_SUBMITTED_ACK_TTL_MS`, 2 minutes) so that a
  // LATER arrival here counts as a fresh one; read during render it would also
  // expire mid-visit, flipping Step 1 from "Start" to "Scan again"/"Submitted"
  // under a user who is part-way through the form — every keystroke in it
  // re-renders this component. Freezing the decision keeps the row stable for
  // the whole visit and lets that TTL be short. A reload is a new mount and
  // re-reads, which is the intended behaviour: a user who reloads the form
  // seconds after asking for it still does not need telling.
  const [acknowledgedAtMount] = useState(
    () => readScanSubmittedMarker()?.acknowledged === true,
  );
  const showSubmittedState = justSubmitted !== null && !acknowledgedAtMount;

  // Counts every arrival at Step 1, whatever the user does next. `hasScanID`
  // false together with a non-null `msSinceLastSuccess` is exactly the empty-
  // form-after-a-success state — the thing the resubmit signature could only
  // see when the user went on to press Submit again, and could not see at all
  // on the back-gesture path, which writes nothing else to pwa_logs. One line
  // per mount, no PII.
  useEffect(() => {
    const marker = readScanSubmittedMarker();
    logInfo("[NewAvatarScreen] mounted", {
      hasScanID: localStorage.getItem("scanID") !== null,
      msSinceLastSuccess: marker ? marker.ageMs : null,
      // Splits that same population in two: `false` is an arrival that saw the
      // submitted state, `true` is one that was deliberate and did not. Without
      // it, a drop in the "Submitted" state's exposure would be indistinguishable
      // from a drop in the underlying empty-form arrivals.
      submittedStateAcknowledged: marker ? marker.acknowledged : null,
      // The acknowledgement's own TTL, so a rate computed from these rows can
      // be re-derived if it ever changes. See SCAN_SUBMITTED_ACK_TTL_MS.
      ackTtlMs: SCAN_SUBMITTED_ACK_TTL_MS,
    });
  }, []);

  // Intercept browser back-button / swipe when a scan is in progress.
  // Without this, navigating back leaves scanID in localStorage, causing
  // AvatarScreen to show a false "Submitting scans" state on next visit.
  useEffect(() => {
    // Capture the URL and RR state at mount time.
    //
    // React Router calculates delta = nextIdx - currentIdx to decide whether to
    // re-render. Pushing guard entries with the SAME rrState (same idx) keeps
    // delta=0 on back-swipe so React Router skips the location update.
    //
    // We push TWO guards so that iOS Safari's native swipe-back animation reveals
    // another /new-avatar-screen entry (guard1) rather than a completely different
    // page, eliminating the jarring flash.
    //
    // We capture the URL at mount time because when popstate fires, the browser has
    // already updated window.location.href to the popped-to URL — using it there
    // would push the guard to the wrong path.
    const rrState = window.history.state;
    const currentUrl = window.location.href;
    let guardsPushed = false;

    const pushGuardsIfReady = () => {
      if (guardsPushed) return;
      if (localStorage.getItem("scanID") !== null) {
        window.history.pushState(rrState, "", currentUrl);
        window.history.pushState(rrState, "", currentUrl);
        guardsPushed = true;
      }
    };

    // ScanUploader sets scanID asynchronously when upload() runs. ScanningScreen's
    // navigation can fire before that happens, so guard installation must wait
    // for the upload to begin if scanID isn't yet present at mount.
    pushGuardsIfReady();
    const cleanupProgress = scanUploader.on(
      UploadEvents.PROGRESS,
      pushGuardsIfReady,
    );
    const cleanupSuccess = scanUploader.on(
      UploadEvents.SUCCESS,
      pushGuardsIfReady,
    );

    const handlePopState = (e: PopStateEvent) => {
      if (localStorage.getItem("scanID") !== null) {
        e.stopImmediatePropagation();
        window.history.pushState(rrState, "", currentUrl);
        setOpen(true);
      }
    };

    window.addEventListener("popstate", handlePopState, true);
    return () => {
      window.removeEventListener("popstate", handlePopState, true);
      cleanupProgress?.();
      cleanupSuccess?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [clientId, setClientId] = useState<string>("");
  const [multiInputDefaultValue, setMultiInputDefaultValue] = useState("");
  const [error, setError] = useState<string | null>("");
  const handleClose = () => {
    setOpen(false);
  };
  const handleCancelModalClose = () => setIsCancelModalOpen(false);
  const handleCancelModalConfirm = () => {
    setIsCancelModalOpen(false);
    const scanID = localStorage.getItem("scanID");
    if (scanID) {
      // Scan exists — discard it, then clear sharing data and navigate home
      setOpen(true);
    } else {
      // No scan. If this is an active store share (has a redirectUrl), return
      // the user to the store via the /share-cancelled screen — that screen
      // resets the widget session and clears the sharing data itself. Otherwise
      // clear here and leave to home explicitly (navigateBackTo, not
      // navigateBack) so cancelling always lands on the Avatars page — with the
      // reverse/back slide — rather than stepping back into a scan step the user
      // just left.
      const { redirectUrl } = getAvatarSharingData();
      if (redirectUrl) {
        navigateTo("/share-cancelled");
        return;
      }
      clearAvatarSharingData();
      navigateBackTo("/");
    }
  };

  let motionDetected = false;
  let motionEventListener: any;

  useEffect(() => {
    if (error) {
      setError(null);
    }
    // eslint-disable-next-line
  }, [clientId]);

  const testMotionSensors = () => {
    const supportsMotionEvent = "DeviceMotionEvent" in window;
    if (isDev) {
      motionDetected = true;
    }

    if (!supportsMotionEvent && !isDev) {
      setCompatible(false);
      return;
    }

    if (
      typeof (DeviceMotionEvent as any).requestPermission === "function" ||
      isDev
    ) {
      setCompatible(true);
      return;
    }
    motionEventListener = (event: DeviceMotionEvent) => {
      if (
        typeof event.acceleration?.x === "number" ||
        typeof event.accelerationIncludingGravity?.x === "number"
      ) {
        motionDetected = true;
        setCompatible(true);
        window.removeEventListener("devicemotion", motionEventListener);
      }
    };

    window.addEventListener("devicemotion", motionEventListener);

    setTimeout(() => {
      if (!motionDetected) {
        setCompatible(false);
        window.removeEventListener("devicemotion", motionEventListener);
      }
    }, 1000);
  };

  useEffect(() => {
    testMotionSensors();
    setScanStatus("");

    // Cleanup: Clear sharing data when user navigates away without completing scan
    // This handles the case when user closes tab or navigates away
    return () => {
      // Only clear if there's no active scan in progress
      const scanID = localStorage.getItem("scanID");
      if (!scanID && !deepLink) {
        // No scan started yet, safe to clear sharing data
        clearAvatarSharingData();
      }
      // If scanID exists, the share outlives the submit (BUG-0012) and is
      // settled the next time this form opens — see the effect below.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BUG-0012: a share now survives a rebuilt page and a submit, so this form
  // is where it gets settled. (1) Once the scan it was used for is approved,
  // it ends — a second scan after a delivered one is not sent automatically,
  // while a redo after a FAILED one is. (2) The banner must be able to name
  // the client: the landing's lookup can time out and nothing else on this
  // path retries it. Both reads are bounded (sharedScanApproval.ts) and run
  // here, never on the boot/splash path (BUG-0011).
  const [, setShareStateVersion] = useState(0);
  useEffect(() => {
    let active = true;
    const settleShare = async () => {
      if (getSharedScanId()) {
        const outcome = await resolveSharedScanShare();
        if (!active) return;
        if (outcome === "cleared") setShareStateVersion((v) => v + 1);
      }
      const { clientId: sharedClientId, orgName } = getAvatarSharingData();
      if (!sharedClientId) return;
      // Liveness first, and never behind a short-circuit: this call is also
      // what ENDS an expired share (the 2-hour window), and the render path
      // skips it whenever the per-tab link flag is present. If it ends one
      // here, re-render — otherwise the banner keeps naming a client whose
      // data is already gone, and the form goes on promising a delivery that
      // cannot happen.
      if (!hasActiveSharingSession()) {
        setShareStateVersion((v) => v + 1);
        return;
      }
      if (
        orgName?.trim() ||
        isMalchusEnv ||
        isNeemsEnv ||
        isStyleMatchEnv ||
        isSisleyEnv
      ) {
        return;
      }
      const name = await resolveShareOrgName(sharedClientId);
      if (!active || !name) return;
      setAvatarSharingData({ orgName: name });
      setShareStateVersion((v) => v + 1);
    };
    settleShare().catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // The Share card's own cancel. The header's Cancel abandons the whole visit
  // through a confirmation dialog and, for a store share, routes via
  // /share-cancelled so the shop's widget is reset. This one is deliberately
  // smaller and reversible: it clears the share in THIS browser only, leaves
  // the shop's session untouched, and offers Undo. The snapshot lives in
  // component state, so leaving the form ends the offer — by then the share is
  // already out of storage and the scan will simply not be shared.
  const [cancelledShare, setCancelledShare] = useState<
    (AvatarSharingData & { sharedScanId: string | null }) | null
  >(null);

  const handleCancelShare = () => {
    const snapshot = {
      ...getAvatarSharingData(),
      sharedScanId: getSharedScanId(),
    };
    clearAvatarSharingData();
    setCancelledShare(snapshot);
    setShareStateVersion((v) => v + 1);
  };

  const handleRestoreShare = () => {
    if (!cancelledShare) return;
    setAvatarSharingData({
      clientId: cancelledShare.clientId,
      orgName: cancelledShare.orgName,
      sessionId: cancelledShare.sessionId,
      shareCode: cancelledShare.shareCode,
      redirectUrl: cancelledShare.redirectUrl,
    });
    // Storing a clientId counts as STARTING a share, which forgets the scan an
    // earlier one was used for (BUG-0012). This is a restore, not a new share,
    // so put that back or the approval check would have nothing to settle.
    if (cancelledShare.sharedScanId) {
      setSharedScanId(cancelledShare.sharedScanId);
    }
    setDeeplinkFlag();
    setCancelledShare(null);
    setShareStateVersion((v) => v + 1);
  };

  const handleClientIdChange = (value: string) => {
    setClientId(value);
  };

  const [newAvatar, setNewAvatar] = useState<Avatar>({
    gender: "",
    firstName: "",
    lastName: "",
    email: "",
    height: "",
    genderPicker: "",
    agePicker: "",
    age: "",
    weight: "",
    heightCmTens: "",
    heightCmOnes: "",
    heightFt: "",
    heightInch: "",
    weightLb: "",
    weightKg: "",
    // Sisley optional fields (empty for other tenants; UI is Sisley-gated).
    accountType: "",
    companyName: "",
    handedness: "",
    waistband: "",
    headCircumference: "",
    handMeasurement: "",
    fingerMeasurement: "",
    notes: "",
  });
  const [completedFieldsCount, setCompletedFieldsCount] = useState(0);

  useEffect(() => {
    const getCompletedFieldsCount = () => {
      let count = 0;
      if (newAvatar.gender) count++;
      if (newAvatar.firstName) count++;
      if (newAvatar.lastName) count++;
      if (newAvatar.email) count++;
      if (newAvatar.height) count++;
      if (newAvatar.age) count++;
      if (newAvatar.weight) count++;
      return count;
    };

    setCompletedFieldsCount(getCompletedFieldsCount());
  }, [newAvatar]); // Recalculate when `newAvatar` changes

  useEffect(() => {
    const newAvatar = getAvatarFromLocalStorage();
    const sharingData = getAvatarSharingData();

    if (newAvatar && newAvatar !== null) {
      setNewAvatar({
        gender: newAvatar.gender,
        firstName: newAvatar.firstName,
        lastName: newAvatar.lastName,
        email: newAvatar.email,
        height: newAvatar.height,
        age: newAvatar.age,
        weight: newAvatar.weight,
        heightCmTens: newAvatar.heightCmTens,
        heightCmOnes: newAvatar.heightCmOnes,
        heightFt: newAvatar.heightFt,
        heightInch: newAvatar.heightInch,
        weightLb: newAvatar.weightLb,
        weightKg: newAvatar.weightKg,
        genderPicker: newAvatar.genderPicker,
        agePicker: newAvatar.agePicker,
        // Preserve the Sisley optional fields across a reload / step-back.
        accountType: newAvatar.accountType || "",
        companyName: newAvatar.companyName || "",
        handedness: newAvatar.handedness || "",
        waistband: newAvatar.waistband || "",
        headCircumference: newAvatar.headCircumference || "",
        handMeasurement: newAvatar.handMeasurement || "",
        fingerMeasurement: newAvatar.fingerMeasurement || "",
        notes: newAvatar.notes || "",
      });
      const convertHeight = localStorage.getItem("convertHeight");
      const convertWeight = localStorage.getItem("convertWeight");

      // These flags record which unit the value was entered in, so they also
      // restore the modals' unit toggles. Without this the form row reads
      // "178cm" while the height modal reopens on the `in` tab — and, because
      // closeHeightModal cleared the feet/inches slots when it committed the cm
      // entry, on an empty field.
      if (convertHeight !== null) {
        const enteredInCm = stringToBoolean(convertHeight);
        setConvertHeight(enteredInCm);
        setUnit(enteredInCm ? "cm" : "inch");
      }
      if (convertWeight !== null) {
        const enteredInKg = stringToBoolean(convertWeight);
        setConvertWeight(enteredInKg);
        setWeightUnit(enteredInKg ? "kg" : "lb");
      }
    }
    if (sharingData.shareCode) {
      setMultiInputDefaultValue(sharingData.shareCode);
    }
  }, []);
  const handlePickerChange = (key: string, value: unknown) => {
    const typedValue = value as Record<string, any>;
    setNewAvatar((prev) => ({ ...prev, [key]: typedValue[key] }));
  };

  const [errors, setErrors] = useState<Errors>({});

  const validate = (): boolean => {
    const tempErrors: Errors = {};
    if (!newAvatar.gender) tempErrors.gender = "Gender is required.";
    if (!newAvatar.firstName) tempErrors.firstName = "First Name is required.";
    if (!newAvatar.lastName) tempErrors.lastName = "Last Name is required.";
    if (!newAvatar.email) {
      tempErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(newAvatar.email)) {
      tempErrors.email = "Email is invalid.";
    }
    if (!newAvatar.height) tempErrors.height = "Height is required.";
    if (!newAvatar.age) tempErrors.age = "Age is required.";
    if (!newAvatar.weight) tempErrors.weight = "Weight is required.";
    const scanID = localStorage.getItem("scanID");
    if (scanID === null) {
      logError(
        "[NewAvatarScreen] validate() — scanID is null in localStorage",
        {
          scanUploaderScanId: scanUploader.getScanId(),
          // This row IS the BUG-0003 metric: 36% of the "Incomplete Steps"
          // banners it drives were shown, in a 13-day prod window, to users
          // whose scan had already succeeded — a join done per user over time,
          // so an acknowledged arrival that legitimately reaches this banner
          // would otherwise be indistinguishable from the bug and would read
          // as a regression in the very fix that removed it. Carry the two
          // fields that separate them, on the row that is counted, not only on
          // the mount row. (`docs/telemetry/PLAN.md` §5.1, `EVENTS.md` §3.5.)
          msSinceLastSuccess: justSubmitted ? justSubmitted.ageMs : null,
          // Read from the same render that decided the Step-1 branch above,
          // so the row describes what the user was actually looking at.
          // `false` here should be structurally impossible — the Submit guard
          // returns before validate() for an unacknowledged marker — so a
          // `false` row means the render and the guard have diverged.
          submittedStateAcknowledged: justSubmitted
            ? acknowledgedAtMount
            : null,
        },
      );
      setScanError(true);
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };
  const validOrg = async (completeID: string) => {
    if (isMalchusEnv) {
      if (completeID !== "715053") return false;
    }
    const clientIdQuery = query(
      collection(db, "clientIds"),
      where("clientId", "==", completeID),
    );

    try {
      const querySnapshot = await getDocs(clientIdQuery);

      if (!querySnapshot.empty) {
        const documentData = querySnapshot.docs[0].data();

        if (documentData.clientId) {
          const orgName = documentData.orgName
            ? documentData.orgName.trim()
            : "";
          const name = documentData.name ? documentData.name.trim() : "";

          if (orgName) {
            setAvatarSharingData({
              clientId: documentData.clientId,
              shareCode: clientId,
              orgName: orgName,
            });
          } else if (name) {
            setAvatarSharingData({
              clientId: documentData.clientId,
              shareCode: clientId,
              orgName: name,
            });
          } else {
            setAvatarSharingData({
              clientId: documentData.clientId,
              shareCode: clientId,
              orgName: "",
            });
          }
          return true;
        }
      } else {
        setError("Invalid share code");
        return false;
      }
    } catch (error) {
      setError("Invalid share code");
      console.error("Error getting documents:", error);
      return false;
    }
  };
  // A new scan starts here, so the just-submitted marker goes: otherwise a user
  // who wants a second avatar would be held on the "Submitted" state below for
  // the rest of its TTL. Shared by the Start button and the "Scan again" link;
  // everything after the clear is the Start button's original handler,
  // unchanged.
  const handleStartScan = () => {
    clearScanSubmittedMarker();
    localStorage.setItem("newAvatar", JSON.stringify(newAvatar));
    localStorage.setItem("convertWeight", convertWeight.toString());
    localStorage.setItem("convertHeight", convertHeight.toString());
    localStorage.setItem("shareCode", clientId);

    navigateTo("/start-scan", {
      state: { deepLink },
    });
  };

  // Submitting is the SUCCESS exit from the `form` phase. It must be closed as
  // `advanced` HERE, before the route change: the `submit` phase is not wired,
  // so nothing on /submit-scan re-asserts a phase, and the deferred
  // `abandonOnUnmount` in the mount effect would otherwise find `form` still
  // open and record every successful submission as an abandonment.
  const goToSubmit = () => {
    exitPhase(Phases.FORM, Outcomes.ADVANCED);
    navigateTo("/submit-scan");
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSubmitted(true);
    const scanID = localStorage.getItem("scanID");
    logInfo("[NewAvatarScreen] handleSubmit() called", {
      scanID,
      scanUploaderScanId: scanUploader.getScanId(),
    });

    // This form's scan has already been submitted — its scanID was correctly
    // cleared on success. Take the user to their avatars list, where the scan
    // is visible as Processing, rather than letting validate() log
    // "scanID is null" and paint the red "Incomplete Steps" banner over work
    // that is already done. (36% of that banner in prod is shown to users whose
    // scan had already succeeded.)
    // Gated on the acknowledged flag, not the raw marker: a user who pressed
    // "New Avatar" and then pressed Submit without scanning has genuinely
    // incomplete steps, and the red banner pointing at the Scan step is the
    // correct answer for them. Bouncing them to the avatars list with no
    // explanation would be the confusing one. Unacknowledged arrivals — the
    // 36% this guard was written for — are unchanged.
    if (scanID === null && showSubmittedState) {
      navigateBackTo("/");
      return;
    }

    if (validate() && scanID !== null) {
      if (clientId.length > 1) {
        if (clientId.length !== 6) {
          setError("Invalid share code");
          return;
        }
        if (await validOrg(clientId)) {
          localStorage.setItem("convertWeight", convertWeight.toString());
          localStorage.setItem("convertHeight", convertHeight.toString());

          localStorage.setItem("newAvatar", JSON.stringify(newAvatar));
          goToSubmit();
          setNewAvatar({
            gender: "",
            firstName: "",
            lastName: "",
            email: "",
            height: "",
            age: "",
            weight: "",
            heightCmTens: "",
            heightCmOnes: "",
            heightFt: "",
            heightInch: "",
            weightLb: "",
            weightKg: "",
            genderPicker: "",
            agePicker: "",
          });
          setErrors({});
        }
      } else {
        localStorage.setItem("convertWeight", convertWeight.toString());
        localStorage.setItem("convertHeight", convertHeight.toString());

        localStorage.setItem("newAvatar", JSON.stringify(newAvatar));
        goToSubmit();
        setNewAvatar({
          genderPicker: "",
          agePicker: "",
          gender: "",
          firstName: "",
          lastName: "",
          email: "",
          height: "",
          age: "",
          weight: "",
          heightCmTens: "",
          heightCmOnes: "",
          heightFt: "",
          heightInch: "",
          weightLb: "",
          weightKg: "",
        });
        setErrors({});
      }
      scanUploader.submitScan(storePhotosEnabled);
    }
  };
  const cancelAgeModal = () => {
    setShowAgeModal(false);
  };
  const cancelGenderModal = () => {
    setShowGenderModal(false);
  };
  const closeGenderModal = () => {
    const value = newAvatar.genderPicker;
    setNewAvatar({
      ...newAvatar,
      gender: newAvatar.genderPicker,
    });

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (value.trim()) {
        delete updatedErrors.gender;
      }
      return updatedErrors;
    });
    setShowGenderModal(false);
  };
  const closeAgeModal = () => {
    const value = newAvatar.agePicker;
    setNewAvatar({
      ...newAvatar,
      age: newAvatar.agePicker,
    });
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (value.trim()) {
        delete updatedErrors.age;
      }
      return updatedErrors;
    });
    setShowAgeModal(false);
  };

  const closeHeightModal = () => {
    if (unit === "inch") {
      if (newAvatar.heightFt !== "") {
        const value = newAvatar.heightFt;

        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          if (value.trim()) {
            delete updatedErrors.height;
          }
          return updatedErrors;
        });
        setConvertHeight(false);
        setNewAvatar({
          ...newAvatar,
          heightCmOnes: "",
          heightCmTens: "",
          height:
            newAvatar.heightFt !== "" && newAvatar.heightInch !== ""
              ? `${newAvatar.heightFt}${newAvatar.heightInch}`
              : newAvatar.heightFt,
        });
      } else {
        setConvertHeight(false);
        setNewAvatar({
          ...newAvatar,
          heightCmOnes: "",
          heightCmTens: "",
          heightFt: "",
          heightInch: "",
          height: "",
        });
      }
    } else {
      if (newAvatar.heightCmTens !== "") {
        const value = newAvatar.heightCmTens;

        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          if (value.trim()) {
            delete updatedErrors.height;
          }
          return updatedErrors;
        });
        setConvertHeight(true);
        setNewAvatar({
          ...newAvatar,
          heightFt: "",
          heightInch: "",
          height: convertCmToFeetInches(
            // parseFloat on BOTH slots: the tens slot now carries the fraction
            // of a typed value (`170.5`), while the ones slot still carries the
            // half step of a pre-2.4.19 wheel draft. parseInt here truncated the
            // former to 170.
            newAvatar.heightCmTens !== "" && newAvatar.heightCmOnes !== ""
              ? parseFloat(newAvatar.heightCmTens) +
                  parseFloat(newAvatar.heightCmOnes)
              : parseFloat(newAvatar.heightCmTens),
          ),
        });
      } else {
        setConvertHeight(false);
        setNewAvatar({
          ...newAvatar,
          heightCmOnes: "",
          heightCmTens: "",
          heightFt: "",
          heightInch: "",
          height: "",
        });
      }
    }

    setShowHeightModal(false);
  };
  const cancelWeightModal = () => {
    setShowWeightModal(false);
  };
  const cancelHeightModal = () => {
    setShowHeightModal(false);
  };
  const closeWeightModal = () => {
    if (weightUnit === "kg") {
      if (newAvatar.weightKg !== "") {
        const value = newAvatar.weightKg;

        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          if (value.trim()) {
            delete updatedErrors.weight;
          }
          return updatedErrors;
        });
        setConvertWeight(true);
        setNewAvatar({
          ...newAvatar,
          weightLb: "",
          weight: convertKgToLb(parseInt(newAvatar.weightKg))
            .toFixed(2)
            .toString(),
        });
      } else {
        setConvertWeight(false);

        setNewAvatar({ ...newAvatar, weight: "" });
      }
    } else {
      if (newAvatar.weightLb !== "") {
        const value = newAvatar.weightLb;

        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          if (value.trim()) {
            delete updatedErrors.weight;
          }
          return updatedErrors;
        });
        setConvertWeight(false);
        setNewAvatar({
          ...newAvatar,
          weightKg: "",

          weight: parseInt(newAvatar.weightLb).toString(),
        });
      } else {
        setConvertWeight(false);

        setNewAvatar({ ...newAvatar, weight: "", weightKg: "", weightLb: "" });
      }
    }
    setShowWeightModal(false);
  };

  return (
    <div
      style={{ background: "var(--sm-bg, rgba(240, 242, 250, 0.7))" }}
      className="main-grid"
    >
      <GenderModal
        newAvatar={newAvatar}
        handlePickerChange={handlePickerChange}
        open={showGenderModal}
        onClose={closeGenderModal}
        onCancel={cancelGenderModal}
      />
      <AgeModal
        onCancel={cancelAgeModal}
        newAvatar={newAvatar}
        handlePickerChange={handlePickerChange}
        open={showAgeModal}
        onClose={closeAgeModal}
      />
      <HeightModal
        onCancel={cancelHeightModal}
        unit={unit}
        setUnit={setUnit}
        newAvatar={newAvatar}
        handlePickerChange={handlePickerChange}
        open={showHeightModal}
        onClose={closeHeightModal}
      />
      <WeightModal
        onCancel={cancelWeightModal}
        newAvatar={newAvatar}
        unit={weightUnit}
        setUnit={setWeightUnit}
        handlePickerChange={handlePickerChange}
        open={showWeightModal}
        onClose={closeWeightModal}
      />
      <CancelModal
        clientName={sharingData.orgName || ""}
        open={isCancelModalOpen}
        onClose={handleCancelModalClose}
        onCancel={handleCancelModalConfirm}
      />
      <DiscardModal open={open} handleClose={handleClose} />

      <div
        className="new-avatar-header-wrap"
        style={{ paddingBottom: isNeemsEnv ? "8px" : "20px" }}
      >
        <div className="new-avatar-header">
          <button
            onClick={() => {
              if (localStorage.getItem("scanID") !== null) {
                // A scan was already taken → go straight to the discard warning
                // (not the "Cancel sending measurements" dialog). Discarding
                // also ends any active share and redirects back to the store —
                // all handled inside DiscardModal. This collapses the old
                // two-modal (cancel-share → discard) chain: once a scan exists,
                // cancelling means discarding the avatar.
                setOpen(true);
              } else if (deepLink) {
                // No scan yet, active share → confirm cancelling the share.
                setIsCancelModalOpen(true);
              } else {
                // No scan, no share → just leave to home with the back slide.
                clearAvatarSharingData();
                navigateBackTo("/");
              }
            }}
            className="cancel"
          >
            Cancel
          </button>
          <div className="new-avatar">New Avatar</div>
          <div className="close-icon"></div>
        </div>
      </div>
      {/* Share indicator: this avatar is being shared to an org (deep-link
          session). Mirrors AvatarSelect's "Sending Avatar to:" banner so the
          user understands the Cancel button abandons that share. Hidden for
          the Malchus/Neems/StyleMatch tenants, matching ShareCard (which
          returns null there). */}
      {deepLink &&
        !isMalchusEnv &&
        !isNeemsEnv &&
        !isStyleMatchEnv &&
        !isSisleyEnv && (
          <div className="new-avatar-share-banner">
            <p className="p2">
              Sending Avatar to: <b>{shareRecipientLabel(sharingData)}</b>
            </p>
          </div>
        )}
      {isSisleyEnv && (
        <img
          style={{
            display: "block",
            margin: "0 auto 24px",
            height: "28px",
            width: "auto",
          }}
          src="/images/sisley-logo.svg"
          alt="Sisley Clothing Australia × TrueToForm"
        />
      )}
      {isNeemsEnv && (
        <img
          style={{
            marginBottom: "24px",
            height: "28px",
          }}
          src="/images/neems-logo.svg"
          alt="Neems"
        />
      )}
      {isStyleMatchEnv && (
        <img
          style={{
            marginBottom: "24px",
            height: "56px",
          }}
          src="/images/stylematch-logo.svg"
          alt="StyleMatch"
        />
      )}
      <div className="avatar-form">
        {/* U4: "Logged in as Guest" is confusing in the embedded StyleMatch
            flow (the user came from their StyleMatch account) — hide it there. */}
        {!isStyleMatchEnv && (
          <div className="status">
            <span>Logged in as</span> <span className="guest">Guest</span>
          </div>
        )}
        <div className="complete-steps">
          <h3>Complete the steps below to generate your new avatar</h3>
        </div>
        {Object.keys(errors).length > 0 || scanError ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: pink[50],
              borderRadius: "16px",
              padding: "10px 20px",
              textAlign: "center",
              margin: "10px 0",
            }}
          >
            <Typography variant="h6" sx={{ color: pink[400] }}>
              Incomplete Steps
            </Typography>
          </Box>
        ) : null}

        {!compatible ? (
          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
            }}
            id="not-supported-device"
          >
            <WarningAmberIcon sx={{ color: "#FFA000", marginRight: "8px" }} />
            <Box>
              <Typography variant="body1" fontWeight="bold">
                This device does not support scanning
              </Typography>
              <Typography variant="body2">
                Use a smartphone or tablet to take a scan
              </Typography>
            </Box>
          </Box>
        ) : null}

        <div className="steps">
          <div className="step">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <span className="step-count">1</span>{" "}
                </Grid>
                <Grid item xs>
                  <span className="step-name">Scan</span>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
                marginBottom: "10px",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <svg
                    className="body-img"
                    width="40"
                    height="41"
                    viewBox="0 0 40 41"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.5 10.5L15.7816 13.5971C15.9264 15.19 15.8315 16.7957 15.5001 18.3605L11.5 37.2497M25.5 10.5L25.2184 13.5971C25.0736 15.19 25.1685 16.7957 25.4999 18.3605L29.5 37.2497M35 15.2495L27.8792 6.66307C27.0055 5.6096 25.7081 5 24.3394 5H24C23.1716 5 22.5 4.32843 22.5 3.5V3.5M6 15.2495L13.1208 6.66321C13.9945 5.60974 15.2919 5.00013 16.6605 5.00008L17 5.00006C17.8284 5.00003 18.5 4.32844 18.5 3.5V3.5M15 37.2497L19.5499 23.3934C19.8511 22.476 21.1489 22.476 21.4501 23.3934L26 37.2497"
                      stroke="black"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </Grid>
                <Grid item xs>
                  <Typography className="text" variant="h6">
                    Body
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    Rotate capture
                  </Typography>
                </Grid>
                <Grid item>
                  {localStorage.getItem("scanID") !== null ? (
                    <p className="completed-text">Complete</p>
                  ) : showSubmittedState ? (
                    // The scan succeeded moments ago, its keys are gone, and
                    // nothing has told this user so. Without this branch the
                    // row falls through to the Start button and tells them,
                    // wrongly, that nothing has happened yet. (A user who
                    // pressed "New Avatar" on their avatars list HAS been
                    // told, and takes the plain Start button below instead —
                    // see `showSubmittedState`.)
                    //
                    // The button stays — it is the same control, doing the same
                    // thing, and it is where anyone wanting another avatar will
                    // look. Only its label changes, so pressing it clearly reads
                    // as starting ANOTHER scan rather than the first one. The
                    // "Submitted" line above it is what says the previous scan
                    // was not lost.
                    <div className="scan-button">
                      <button
                        onClick={handleStartScan}
                        className="start"
                        disabled={!compatible}
                        // .start pins width to 84px for the one-word "Start",
                        // so a longer label spills outside the pill. Size to
                        // the text instead, and tighten the 24px side padding
                        // to 16px: measured on the real row, the default
                        // padding makes this button 136px wide, which squeezes
                        // the middle column to 122px and wraps "Rotate capture"
                        // onto two lines. 16px keeps it on one.
                        style={{ width: "auto", padding: "8px 16px" }}
                      >
                        Scan again
                      </button>
                      {/* Reads as a caption on the button above it, so the
                          margins go: <p> carries a 1em default top and bottom
                          that would otherwise open a ~16px gap. */}
                      <p
                        className="completed-text"
                        style={{ margin: "2px 0 0", textAlign: "center" }}
                      >
                        Submitted
                      </p>
                    </div>
                  ) : (
                    <div className="scan-button">
                      <span>
                        {" "}
                        <button
                          onClick={handleStartScan}
                          className="start"
                          disabled={!compatible}
                        >
                          Start
                        </button>
                      </span>
                      {scanError ? (
                        <span>
                          {" "}
                          <ErrorIcon className="error-outline" />
                        </span>
                      ) : null}
                    </div>
                  )}
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
                backgroundColor: "var(--sm-bg, rgba(240, 242, 250, 0.7))",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <CustomInfoShieldIcon />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#101012",
                      fontSize: "15px",
                      letterSpacing: "0px",
                    }}
                  >
                    Scans do not store photos by default
                  </Typography>
                  <Typography
                    sx={{ color: "var(--sm-text-muted, #8b8b97)" }}
                    variant="body2"
                    color="textSecondary"
                  >
                    Learn more about{" "}
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.truetoform.fit/help-center#scan-data-privacy"
                      underline="always"
                      sx={{ color: "var(--sm-primary)" }}
                    >
                      scan data and privacy
                    </Link>
                    .
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
              }}
            >
              <Grid container spacing={5} alignItems="center">
                <Grid item></Grid>
                <Grid item xs>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 400,
                      color: "var(--sm-text-muted, #8b8b97)",
                      fontSize: "15px",
                      letterSpacing: "0px",
                    }}
                  >
                    Need scan help? Visit our{" "}
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      sx={{ color: "var(--sm-primary)" }}
                      href="https://www.truetoform.fit/help-center#scanning-tips"
                    >
                      help center
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </div>
          <div className="step">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <span className="step-count">2</span>{" "}
                </Grid>
                <Grid item xs>
                  <span className="step-name">Avatar info</span>
                </Grid>
                <Grid
                  item
                  className={
                    (submitted && completedFieldsCount < 7) ||
                    (submitted && Object.keys(errors).length !== 0)
                      ? "errorcount"
                      : ""
                  }
                >
                  {completedFieldsCount}/7
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  fontSize: "12px",
                  lineHeight: "24px",
                  fontWeight: 700,
                  color: "var(--sm-text-muted, rgba(140, 153, 200, 1))",
                }}
              >
                CONTACT INFO
              </Typography>
              <TextField
                label={newAvatar.firstName ? "First Name" : ""}
                error={!!errors.firstName}
                helperText={errors.firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewAvatar({ ...newAvatar, firstName: value });

                  setErrors((prevErrors) => {
                    const updatedErrors = { ...prevErrors };
                    if (value.trim()) {
                      delete updatedErrors.firstName;
                    }
                    return updatedErrors;
                  });
                }}
                required
                value={newAvatar.firstName ? newAvatar.firstName : ""}
                placeholder="First Name"
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />

              <TextField
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
                value={newAvatar.lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewAvatar({ ...newAvatar, lastName: value });

                  setErrors((prevErrors) => {
                    const updatedErrors = { ...prevErrors };
                    if (value.trim()) {
                      delete updatedErrors.lastName;
                    }
                    return updatedErrors;
                  });
                }}
                placeholder="Last Name"
                label={newAvatar.lastName ? "Last Name" : ""}
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />

              <TextField
                error={!!errors.email}
                helperText={errors.email}
                required
                onChange={(e) => {
                  const value = e.target.value;
                  setNewAvatar({ ...newAvatar, email: value });

                  setErrors((prevErrors) => {
                    const updatedErrors = { ...prevErrors };
                    if (value.trim()) {
                      delete updatedErrors.email;
                    }
                    return updatedErrors;
                  });
                }}
                value={newAvatar.email}
                placeholder="Email"
                label={newAvatar.email ? "Email" : ""}
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />
            </Box>

            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography
                color="textSecondary"
                sx={{
                  fontSize: "12px",
                  lineHeight: "24px",
                  fontWeight: 700,
                  color: "var(--sm-text-muted, rgba(140, 153, 200, 1))",
                }}
                variant="caption"
              >
                AVATAR GENERATION INFO
              </Typography>
              <TextField
                error={!!errors.gender}
                helperText={errors.gender}
                required
                value={newAvatar.gender}
                onClick={() => {
                  setShowGenderModal(true);
                }}
                placeholder="Gender"
                variant="standard"
                label={newAvatar.gender ? "Gender" : ""}
                fullWidth
                margin="normal"
                size="small"
              />

              <TextField
                error={!!errors.height}
                helperText={errors.height}
                required
                onClick={() => {
                  setShowHeightModal(true);
                }}
                value={
                  // Guard on a height actually being set so the field shows the
                  // "Height" placeholder when empty. Without this, the metric
                  // default (convertHeight=true on the Sisley build before any
                  // entry) rendered a bare "cm" in the empty field.
                  !newAvatar.height
                    ? ""
                    : convertHeight
                      ? `${newAvatar.heightCmOnes}` === "0.5"
                        ? `${
                            parseInt(newAvatar.heightCmTens) +
                            parseFloat(newAvatar.heightCmOnes)
                          }cm`
                        : `${newAvatar.heightCmTens}cm`
                      : newAvatar.height
                }
                label={newAvatar.height ? "Height" : ""}
                placeholder="Height"
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />

              <TextField
                error={!!errors.age}
                helperText={errors.age}
                required
                onClick={() => {
                  setShowAgeModal(true);
                }}
                value={newAvatar.age}
                label={newAvatar.age ? "Age" : ""}
                placeholder="Age"
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />

              <TextField
                error={!!errors.weight}
                helperText={errors.weight}
                required
                onClick={() => {
                  setShowWeightModal(true);
                }}
                value={
                  convertWeight && newAvatar.weight.length > 0
                    ? `${newAvatar.weightKg}kg`
                    : !convertWeight && newAvatar.weight.length > 0
                      ? `${newAvatar.weightLb}lbs`
                      : ""
                }
                label={newAvatar.weight ? "Weight" : ""}
                placeholder="Weight"
                variant="standard"
                fullWidth
                margin="normal"
                size="small"
              />
            </Box>

            {/* Sisley-only optional Avatar-info fields (Body Scan Procedures
                doc). All optional — they do NOT count toward the 7 required
                fields above, and the whole block is inert on every other
                tenant. Values persist on the newAvatar object and are written
                additively to scans/{id} (see ScanModel.create). */}
            {isSisleyEnv && (
              <Box sx={{ padding: 2 }}>
                <Typography
                  color="textSecondary"
                  sx={{
                    fontSize: "12px",
                    lineHeight: "24px",
                    fontWeight: 700,
                    color: "var(--sm-text-muted, rgba(140, 153, 200, 1))",
                  }}
                  variant="caption"
                >
                  ADDITIONAL INFO (OPTIONAL)
                </Typography>

                <TextField
                  select
                  value={newAvatar.accountType || ""}
                  onChange={(e) =>
                    setNewAvatar({
                      ...newAvatar,
                      accountType: e.target.value,
                      // Clear the company name when switching away from Company
                      // so a stale value isn't persisted/submitted.
                      companyName:
                        e.target.value === "Company"
                          ? newAvatar.companyName || ""
                          : "",
                    })
                  }
                  label="Ordering as"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                >
                  <MenuItem value="Personal">Personal</MenuItem>
                  <MenuItem value="Company">Company</MenuItem>
                </TextField>

                {newAvatar.accountType === "Company" && (
                  <TextField
                    value={newAvatar.companyName || ""}
                    onChange={(e) =>
                      setNewAvatar({
                        ...newAvatar,
                        companyName: e.target.value,
                      })
                    }
                    label={newAvatar.companyName ? "Company name" : ""}
                    placeholder="Company name"
                    variant="standard"
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                )}

                <TextField
                  select
                  value={newAvatar.handedness || ""}
                  onChange={(e) =>
                    setNewAvatar({ ...newAvatar, handedness: e.target.value })
                  }
                  label="Dominant hand"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                >
                  <MenuItem value="Left">Left handed</MenuItem>
                  <MenuItem value="Right">Right handed</MenuItem>
                </TextField>

                <TextField
                  select
                  value={newAvatar.waistband || ""}
                  onChange={(e) =>
                    setNewAvatar({ ...newAvatar, waistband: e.target.value })
                  }
                  label="Waistband type"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                >
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Mid">Mid</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </TextField>

                <TextField
                  value={newAvatar.headCircumference || ""}
                  onChange={(e) =>
                    setNewAvatar({
                      ...newAvatar,
                      headCircumference: e.target.value,
                    })
                  }
                  label={newAvatar.headCircumference ? "Head circumference" : ""}
                  placeholder="Head circumference (cm)"
                  helperText="Around the fullest part, just above the brow (for helmets)"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                />

                <TextField
                  value={newAvatar.handMeasurement || ""}
                  onChange={(e) =>
                    setNewAvatar({
                      ...newAvatar,
                      handMeasurement: e.target.value,
                    })
                  }
                  label={newAvatar.handMeasurement ? "Hand" : ""}
                  placeholder="Hand (cm)"
                  helperText="Around the palm, excluding the thumb (for gloves)"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                />

                <TextField
                  value={newAvatar.fingerMeasurement || ""}
                  onChange={(e) =>
                    setNewAvatar({
                      ...newAvatar,
                      fingerMeasurement: e.target.value,
                    })
                  }
                  label={newAvatar.fingerMeasurement ? "Finger" : ""}
                  placeholder="Finger (cm)"
                  helperText="Around the fullest part of the finger (for gloves)"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  size="small"
                />

                <TextField
                  value={newAvatar.notes || ""}
                  onChange={(e) =>
                    setNewAvatar({ ...newAvatar, notes: e.target.value })
                  }
                  label={newAvatar.notes ? "Additional notes" : ""}
                  placeholder="Additional notes"
                  variant="standard"
                  fullWidth
                  multiline
                  minRows={2}
                  margin="normal"
                  size="small"
                />
              </Box>
            )}
          </div>
          <ShareCard
            deepLink={deepLink}
            handleClientIdChange={handleClientIdChange}
            multiInputDefaultValue={multiInputDefaultValue}
            error={error}
            shareCancelled={cancelledShare !== null}
            onCancelShare={handleCancelShare}
            onRestoreShare={handleRestoreShare}
          />
          <div className="step">
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                padding: 2,
              }}
            >
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item>
                  <Checkbox
                    checked={storePhotosEnabled}
                    onChange={(e) => setStorePhotosEnabled(e.target.checked)}
                    sx={{ p: 0 }}
                    inputProps={{
                      "aria-label":
                        "Attach photos to my scan to help improve accuracy",
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <span className="step-name-sm">
                    Attach photos to my scan to help improve accuracy
                  </span>
                </Grid>
                <Grid item>Optional</Grid>
              </Grid>
            </Box>
            <Box sx={{ padding: 2 }}>
              <Typography
                sx={{
                  fontWeight: "400",
                  color: "var(--sm-text-muted, rgba(138, 138, 152, 1))",
                }}
                variant="body1"
                component="div"
              >
                This also lets our team troubleshoot any issues with your
                scan, and lets anyone you share your avatar with see your
                photos to help improve your fit. You can delete your data or
                opt out at any time.{" "}
                <Link
                  href="https://www.truetoform.fit/help-center#scan-data-privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </Link>
              </Typography>
            </Box>
          </div>
        </div>

        <div className="new-avatar-footer-container">
          <button onClick={handleSubmit} className="primary-button">
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewAvatarScreen;
